import { useCallback, useEffect, useRef, useState } from "react"
import { useHITLStore } from "../store/hitl.store"
import { markNewAttempt } from "../services/draft-storage.service"
import { fetchRewriteSession, cancelRewriteSession, type RewriteSessionContext } from "../services/rewrite-session.service"
import type { RewriteAgentState, CheckpointMessage } from "./useOIORewriteAgent"

export type Decision =
  | { type: "approve" }
  | { type: "reject"; message?: string }
  | { type: "edit"; edited_action: { name: string; args: Record<string, unknown> } }
  // respond: skips persist_commit execution; delivers message as ToolMessage so
  // the model reads user feedback and revises the proposal. Correct for "steer".
  | { type: "respond"; message: string }

function generateUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID()
  }
  // Fallback for non-secure HTTP contexts
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

const INITIAL_STATE: RewriteAgentState = {
  rewrite_status: "idle",
  current_stage: "",
  todos: [],
  updated_oio_draft: null,
  artifact_blob_url: null,
  draft_revision: null,
  changed_ranges: null,
  error_message: null,
  messages: [],
}

async function* parseAGUIEvents(
  stream: ReadableStream<Uint8Array>
): AsyncGenerator<{ type: string; data: Record<string, unknown> }> {
  const reader = stream.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split("\n")
      buffer = lines.pop() ?? ""
      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith("data:")) continue
        const raw = trimmed.slice(5).trim()
        if (!raw || raw === "[DONE]") continue
        try {
          const parsed = JSON.parse(raw) as Record<string, unknown>
          const type = parsed.type as string | undefined
          if (type) yield { type, data: parsed }
        } catch {
          // malformed JSON — skip
        }
      }
    }
  } finally {
    await reader.cancel()
  }
}

interface RewriteStreamOptions {
  threadId: string | undefined
  currentTabId?: string
  /**
   * The AG-UI stream route. Defaults to the OIO rewrite proxy
   * (`/api/v1/rewrite/stream`); the Order-in-Appeal chat passes
   * `/api/v1/rewrite/oia/stream`. Every option below defaults to the OIO value,
   * so an OIO caller that passes none of them behaves exactly as before.
   */
  streamPath?: string
  /** Base of the session routes (`…/{threadId}`, `…/{threadId}/cancel`). */
  sessionPath?: string
  /** The run-marker agent name for new-attempt (`oio_rewrite`). */
  agentName?: string
  /**
   * The localStorage key of this thread's run-id latch. Pass a STABLE function
   * (module level). Another order type names its key by its own per-case
   * convention so deleting a case purges it (`purgeCaseClientState`).
   */
  runIdStorageKey?: (threadId: string) => string
  /**
   * Extra headers for every request this hook sends to the stream and session
   * routes, read at send time. The Order-in-Appeal chat sends the officer's
   * bearer token: its sidecar routes ask the gateway whether he owns the
   * thread before anything reaches oio_service. OIO passes none, and its
   * requests are exactly what they were.
   */
  requestHeaders?: () => Record<string, string>
}

const DEFAULT_REWRITE_STREAM_PATH = "/api/v1/rewrite/stream"
const DEFAULT_REWRITE_AGENT_NAME = "oio_rewrite"

interface RewriteStreamHandle {
  state: RewriteAgentState
  messages: CheckpointMessage[]
  running: boolean
  error: string | null
  currentRunId: string | null
  run: (prompt?: string) => Promise<void>
  reattach: () => Promise<void>
  resume: (decisions: Decision[]) => Promise<void>
  cancel: () => Promise<void>
}

function rewriteRunIdStorageKey(threadId: string): string {
  return `oio-rewrite-run-id:${threadId}`
}

function isValidRewriteRunId(runId: string | null | undefined): runId is string {
  if (typeof runId !== "string") return false
  const trimmed = runId.trim()
  return trimmed.length > 0 && trimmed !== "unknown"
}

function makeRewriteResumeError(): string {
  return "Rewrite resume data was lost after refresh. Start the rewrite again from chat."
}

function loadPersistedRunId(
  threadId: string | undefined,
  keyFor: (threadId: string) => string = rewriteRunIdStorageKey,
): string | null {
  if (!threadId || typeof window === "undefined") return null
  try {
    const runId = window.localStorage.getItem(keyFor(threadId))
    return isValidRewriteRunId(runId) ? runId : null
  } catch {
    return null
  }
}

function persistRunId(
  threadId: string | undefined,
  runId: string,
  keyFor: (threadId: string) => string = rewriteRunIdStorageKey,
): void {
  if (!threadId || typeof window === "undefined") return
  if (!isValidRewriteRunId(runId)) return
  try {
    window.localStorage.setItem(keyFor(threadId), runId)
  } catch {
    // non-fatal
  }
}

function clearPersistedRunId(
  threadId: string | undefined,
  keyFor: (threadId: string) => string = rewriteRunIdStorageKey,
): void {
  if (!threadId || typeof window === "undefined") return
  try {
    window.localStorage.removeItem(keyFor(threadId))
  } catch {
    // non-fatal
  }
}

function mapRewriteSessionStatus(session: RewriteSessionContext): RewriteAgentState["rewrite_status"] {
  if (session.status === "running") return "running"
  if (session.status === "awaiting_human_decision") return "awaiting_approval"
  if (session.status === "terminal") {
    return session.terminalError ? "error" : "done"
  }
  return "idle"
}

function normalizeMessageContent(content: unknown): string {
  if (typeof content === "string") return content
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (!part || typeof part !== "object") return ""
        const text = (part as { text?: unknown }).text
        return typeof text === "string" ? text : ""
      })
      .filter(Boolean)
      .join("")
  }
  if (content && typeof content === "object") {
    const text = (content as { text?: unknown }).text
    if (typeof text === "string") return text
  }
  if (content == null) return ""
  return JSON.stringify(content)
}

function mapSessionMessages(
  messages: Array<{ id: string; role: "user" | "assistant"; content: string }>
): CheckpointMessage[] {
  return messages
    .filter((message) => message.role === "user" || message.role === "assistant")
    .map((message) => ({
      id: message.id,
      type: message.role === "user" ? ("human" as const) : ("ai" as const),
      content: message.content,
    }))
}

function appendTextDelta(
  messages: CheckpointMessage[],
  messageId: string,
  delta: string
): CheckpointMessage[] {
  const existingIdx = messages.findIndex((message) => message.id === messageId)
  if (existingIdx === -1) {
    return [...messages, { id: messageId, type: "ai" as const, content: delta }]
  }

  const updated = [...messages]
  const existing = updated[existingIdx]
  const existingContent =
    typeof existing.content === "string"
      ? existing.content
      : normalizeMessageContent(existing.content)
  updated[existingIdx] = {
    ...existing,
    content: existingContent + delta,
  }
  return updated
}

export function useRewriteStream({
  threadId,
  currentTabId,
  streamPath = DEFAULT_REWRITE_STREAM_PATH,
  sessionPath,
  agentName = DEFAULT_REWRITE_AGENT_NAME,
  runIdStorageKey = rewriteRunIdStorageKey,
  requestHeaders,
}: RewriteStreamOptions): RewriteStreamHandle {
  // Read at send time through a ref, so a new token (or a new function
  // identity) never re-creates the callbacks below.
  const requestHeadersRef = useRef(requestHeaders)
  requestHeadersRef.current = requestHeaders
  const [state, setState] = useState<RewriteAgentState>(INITIAL_STATE)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentRunId, setCurrentRunId] = useState<string | null>(() => loadPersistedRunId(threadId, runIdStorageKey))

  const setProposal = useHITLStore((s) => s.setProposal)
  const setShowRejectFollowup = useHITLStore((s) => s.setShowRejectFollowup)
  const setIsApplying = useHITLStore((s) => s.setIsApplying)
  const runIdRef = useRef<string | null>(loadPersistedRunId(threadId, runIdStorageKey))

  // Captured diff for use in edit-approve so closures don't stale
  const currentDiffRef = useRef<string>("")

  // Track the previous proposal's summary to detect retries when the same summary reappears
  // after a dismissal (likely because the previous approval attempt failed)
  const previousSummaryRef = useRef<string>("")

  // Forward ref to allow HITL handlers to call the resume function
  // before it is constructed (avoids circular closure dependency).
  const resumeRef = useRef<(decisions: Decision[]) => Promise<void>>(async () => {/* noop until mounted */})

  // Forward ref for cancel function (same pattern as resumeRef)
  const cancelRef = useRef<() => Promise<void>>(async () => {/* noop until mounted */})

  const refreshFromSession = useCallback(async (shouldAbort: () => boolean = () => false) => {
    if (!threadId) return
    // The session routes take a base only when one was given, so the OIO call
    // is the exact call it always was.
    const extraHeaders = requestHeadersRef.current?.()
    const session = sessionPath
      ? await fetchRewriteSession(threadId, sessionPath, extraHeaders)
      : await fetchRewriteSession(threadId)
    if (shouldAbort()) return
    const seededMessages = mapSessionMessages(session.messages)
    const nextRewriteStatus = mapRewriteSessionStatus(session)

    setState((prev) => ({
      ...prev,
      rewrite_status: nextRewriteStatus,
      messages: seededMessages,
      error_message: session.terminalError ?? null,
      // Hydrate the last committed draft/highlights straight from the
      // checkpoint on refresh/reattach, instead of leaving them blank until
      // the next live stream event repopulates them. Overwritten directly
      // (not merged with prev) to match rewrite_status/messages above — the
      // session fetch is per-threadId and this hook's state persists across
      // thread switches, so falling back to prev here would leak a
      // previous document's draft/highlights into the newly active one.
      artifact_blob_url: session.artifactBlobUrl,
      updated_oio_draft: session.updatedOioDraft,
      draft_revision: session.draftRevision,
      changed_ranges: session.changedRanges,
    }))
    setRunning(session.status === "running")
    setError(session.terminalError)

    if (session.status === "running" || session.status === "awaiting_human_decision") {
      if (session.runId) {
        runIdRef.current = session.runId
        setCurrentRunId(session.runId)
        persistRunId(threadId, session.runId, runIdStorageKey)
      }
    } else {
      clearPersistedRunId(threadId, runIdStorageKey)
      runIdRef.current = null
      setCurrentRunId(null)
    }

    if (session.status === "awaiting_human_decision" && session.hitlPayload && typeof session.hitlPayload === "object") {
      const payload = session.hitlPayload as Record<string, unknown>
      const actionRequests = payload.action_requests as
        | Array<{ args?: { diff_preview?: string; change_summary?: string } }>
        | undefined
      const diff = actionRequests?.[0]?.args?.diff_preview ?? ""
      const summary = actionRequests?.[0]?.args?.change_summary ?? ""

      // Detect if this proposal is identical to the one shown just before dismissal,
      // which indicates the previous approval likely failed and the LLM re-proposed.
      const isLikelyRetry = Boolean(summary && summary === previousSummaryRef.current)

      setProposal({
        summary,
        diff,
        possibleRetryAfterFailure: isLikelyRetry,
        onApprove: () => void resumeRef.current([{ type: "approve" }]),
        onReject: (message?: string) => {
          setShowRejectFollowup(true)
          void resumeRef.current([{ type: "reject", ...(message ? { message } : {}) }])
        },
        onEditApprove: (editedSummary: string) => {
          void resumeRef.current([{
            type: "edit",
            edited_action: {
              name: "persist_commit",
              args: { change_summary: editedSummary, diff_preview: diff },
            },
          }])
        },
        onSteer: (steerMessage: string) => {
          void resumeRef.current([{ type: "respond", message: steerMessage }])
        },
        onCancel: () => cancelRef.current(),
      })
      // Update the previous summary ref for next comparison
      previousSummaryRef.current = summary
    } else {
      setProposal(null)
    }
  }, [threadId, setProposal, setShowRejectFollowup, sessionPath, runIdStorageKey])

  useEffect(() => {
    if (!threadId) return
    let cancelled = false
    void refreshFromSession(() => cancelled)
    return () => {
      cancelled = true
    }
  }, [threadId, refreshFromSession])

  const processStream = useCallback(
    async (response: Response) => {
      if (!response.ok || !response.body) {
        let msg = `Stream failed: ${response.status}`
        if (response.status === 409 || response.status === 400) {
          try {
            const body = await response.json()
            if (body && body.detail) {
              msg = body.detail
            }
          } catch {
            // ignore
          }
        }
        setError(msg)
        setState((prev) => ({ ...prev, error_message: msg }))
        setRunning(false)
        return
      }

      for await (const event of parseAGUIEvents(response.body)) {
        switch (event.type) {
          case "RUN_STARTED": {
            const runId = (event.data.runId ?? event.data.run_id) as string | undefined
            if (isValidRewriteRunId(runId)) {
              runIdRef.current = runId
              setCurrentRunId(runId)
              persistRunId(threadId, runId, runIdStorageKey)
            }
            setRunning(true)
            setError(null)
            // Eagerly mark status so OIOWorkspace flash animation can detect
            // the running→done transition from STATE_SNAPSHOT after approval.
            setState((prev) => ({ ...prev, rewrite_status: "running" }))
            break
          }

          case "STATE_SNAPSHOT": {
            const snapshot = event.data.snapshot as Partial<RewriteAgentState> | undefined
            if (snapshot) {
              setState((prev) => ({ ...prev, ...snapshot }))
              // If the commit completed, dismiss any leftover HITL panel.
              if (snapshot.rewrite_status === "done") {
                clearPersistedRunId(threadId, runIdStorageKey)
                runIdRef.current = null
                setCurrentRunId(null)
                setProposal(null)
                setIsApplying(false)
              } else if (snapshot.rewrite_status === "error") {
                clearPersistedRunId(threadId, runIdStorageKey)
                runIdRef.current = null
                setCurrentRunId(null)
                setIsApplying(false)
              }
            }
            break
          }

          case "INTERRUPT":
          case "CUSTOM": {
            // ag_ui_langgraph encodes HITL interrupts as CUSTOM events with
            // rawEvent.value.action_requests (deepagents HumanInTheLoop middleware).
            // Some versions use the INTERRUPT type directly.
            let interruptValue: Record<string, unknown> | undefined

            if (event.type === "CUSTOM") {
              const rawEvent = event.data.rawEvent as { value?: Record<string, unknown> } | undefined
              interruptValue = rawEvent?.value
            } else {
              interruptValue =
                (event.data.interrupt as { value?: Record<string, unknown> } | undefined)?.value ??
                (event.data.value as Record<string, unknown> | undefined)
            }

            if (!interruptValue) break

            const actionRequests = interruptValue.action_requests as Array<{
              name: string
              args: { diff_preview: string; change_summary: string }
            }> | undefined

            if (!actionRequests?.length) break
            const { diff_preview, change_summary } = actionRequests[0].args
            currentDiffRef.current = diff_preview ?? ""
            const summary = change_summary ?? ""

            // Detect if this proposal is identical to the one shown just before dismissal,
            // which indicates the previous approval likely failed and the LLM re-proposed.
            const isLikelyRetry = Boolean(summary && summary === previousSummaryRef.current)

            // A fresh proposal means we're back in review — not applying — even
            // if this is a re-propose after a failed commit.
            setIsApplying(false)
            setProposal({
              summary,
              diff: diff_preview ?? "",
              possibleRetryAfterFailure: isLikelyRetry,
              onApprove: () => void resumeRef.current([{ type: "approve" }]),
              onReject: (message?: string) => {
                setShowRejectFollowup(true)
                void resumeRef.current([{ type: "reject", ...(message ? { message } : {}) }])
              },
              onEditApprove: (editedSummary: string) => {
                void resumeRef.current([{
                  type: "edit",
                  edited_action: {
                    name: "persist_commit",
                    args: { change_summary: editedSummary, diff_preview: currentDiffRef.current },
                  },
                }])
              },
              onSteer: (steerMessage: string) => {
                // respond: deepagents skips persist_commit execution and delivers
                // steerMessage as the ToolMessage result. Model reads the feedback
                // and re-proposes. Distinct from reject (which sends an error).
                void resumeRef.current([{ type: "respond", message: steerMessage }])
              },
              onCancel: () => cancelRef.current(),
            })

            // Update the previous summary ref for next comparison
            previousSummaryRef.current = summary
            break
          }

          case "TEXT_MESSAGE_CONTENT": {
            const delta = (event.data.delta ?? event.data.content ?? event.data.text) as string | undefined
            const messageId = (event.data.messageId ?? event.data.message_id ?? event.data.id) as string | undefined
            if (!delta || !messageId) break
            setState((prev) => ({ ...prev, messages: appendTextDelta(prev.messages, messageId, delta) }))
            break
          }

          case "MESSAGES_SNAPSHOT": {
            const msgs = (event.data.messages as Array<{ id?: string; role: string; content: unknown }> | undefined) ?? []
            const mapped: CheckpointMessage[] = msgs
              .filter((m) => m.role === "user" || m.role === "assistant")
              .map((m) => ({
                id: (m.id as string | undefined) ?? `msg-${Date.now()}`,
                type: m.role === "user" ? ("human" as const) : ("ai" as const),
                content: normalizeMessageContent(m.content),
              }))
            setState((prev) => ({ ...prev, messages: mapped }))
            break
          }

          case "RUN_FINISHED":
            setRunning(false)
            break

          case "RUN_ERROR": {
            const msg = (event.data.message as string | undefined) ?? "Rewrite run failed"
            clearPersistedRunId(threadId, runIdStorageKey)
            setError(msg)
            setState((prev) => ({ ...prev, error_message: msg }))
            runIdRef.current = null
            setCurrentRunId(null)
            setRunning(false)
            break
          }

          default:
            break
        }
      }

      setRunning(false)
    },
    [threadId, setProposal, setShowRejectFollowup, setIsApplying, runIdStorageKey]
  )

  const start = useCallback(async (prompt?: string) => {
    if (!threadId) return
    if (!prompt?.trim()) {
      const msg = "Rewrite request is missing a prompt. Start the rewrite again from chat."
      setError(msg)
      setState((prev) => ({ ...prev, error_message: msg }))
      setRunning(false)
      return
    }
    setRunning(true)
    setError(null)
    setShowRejectFollowup(false)
    setIsApplying(false)
    setState((prev) => ({ ...prev, error_message: null }))

    // Signal RunCoordinator to start a fresh run rather than reattaching to
    // the previous terminal/error run for this thread.
    try {
      await markNewAttempt(agentName, threadId)
    } catch {
      // non-fatal — coordinator will reattach if marker is missing
    }

    const userMessages = [{ id: generateUUID(), role: "user", content: prompt }]
    const nextRunId = generateUUID()
    runIdRef.current = nextRunId
    setCurrentRunId(nextRunId)
    persistRunId(threadId, nextRunId, runIdStorageKey)
    setState((prev) => ({
      ...prev,
      rewrite_status: "running",
      messages: [
        ...prev.messages,
        {
          id: `temp-${Date.now()}`,
          type: "human",
          content: prompt,
        },
      ],
    }))

    const response = await fetch(streamPath, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(currentTabId ? { "x-tab-id": currentTabId } : {}),
        ...(requestHeadersRef.current?.() ?? {}),
      },
      body: JSON.stringify({
        threadId,
        runId: nextRunId,
        state: {},
        messages: userMessages,
        tools: [],
        context: [],
        forwardedProps: {},
      }),
    })
    await processStream(response)
  }, [threadId, processStream, setShowRejectFollowup, setIsApplying, currentTabId, streamPath, agentName, runIdStorageKey])

  const reattach = useCallback(async () => {
    if (!threadId) return
    setRunning(true)
    setError(null)
    await refreshFromSession()
  }, [threadId, refreshFromSession])

  const resumeWithDecisions = useCallback(
    async (decisions: Decision[]) => {
      if (!threadId) return
      const resumeRunId = runIdRef.current ?? loadPersistedRunId(threadId, runIdStorageKey)
      if (!isValidRewriteRunId(resumeRunId)) {
        const msg = makeRewriteResumeError()
        setError(msg)
        setState((prev) => ({ ...prev, error_message: msg }))
        setRunning(false)
        return
      }

      // approve/edit commit the draft — the panel closes now but the commit is
      // still in flight, so flag "applying" for the running banner. reject/
      // respond don't commit, so they stay in the ordinary "revising" copy
      // (setting false here also clears any flag left over from a prior turn).
      const isCommitting = decisions.some((d) => d.type === "approve" || d.type === "edit")
      setIsApplying(isCommitting)

      setProposal(null)
      setRunning(true)
      setError(null)
      // Optimistically mark running so the panel→banner handoff has no blank
      // gap: without this, rewrite_status only flips to "running" when the
      // stream's RUN_STARTED arrives a round-trip later, leaving the officer
      // staring at an unchanged order with no indicator right after approving.
      setState((prev) => ({ ...prev, error_message: null, rewrite_status: "running" }))
      runIdRef.current = resumeRunId
      setCurrentRunId(resumeRunId)

      const response = await fetch(streamPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentTabId ? { "x-tab-id": currentTabId } : {}),
          ...(requestHeadersRef.current?.() ?? {}),
        },
        body: JSON.stringify({
          threadId,
          runId: resumeRunId,
          state: {},
          messages: [],
          tools: [],
          context: [],
          forwardedProps: { command: { resume: { decisions } } },
        }),
      })
      await processStream(response)
    },
    [threadId, setProposal, setIsApplying, processStream, currentTabId, streamPath, runIdStorageKey]
  )

  const cancelRewrite = useCallback(async () => {
    if (!threadId) return
    try {
      if (sessionPath) await cancelRewriteSession(threadId, sessionPath, requestHeadersRef.current?.())
      else await cancelRewriteSession(threadId)
    } catch {
      // non-fatal
    }
    clearPersistedRunId(threadId, runIdStorageKey)
    runIdRef.current = null
    setCurrentRunId(null)
    setProposal(null)
    setError(null)
    setState((prev) => ({ ...prev, error_message: null }))
  }, [threadId, setProposal, sessionPath, runIdStorageKey])

  // Keep the ref in sync so HITL closures always call the latest version.
  resumeRef.current = resumeWithDecisions
  cancelRef.current = cancelRewrite

  return {
    state,
    messages: state.messages,
    running,
    error,
    currentRunId,
    run: start,
    reattach,
    resume: resumeWithDecisions,
    cancel: cancelRewrite,
  }
}
